import getByPath from 'lodash/get';
import { createSelector } from 'reselect';
import { generateObjectId, joyDocHelper, tableHelper, generateReadableTableColumnId, generateReadableId, validateReadableId, validateObjectId } from '@builttocreate/engine-utils';

import { getPageOrder } from '@builttocreate/engine-utils/dist/joyDocHelper';
import FileViews from '../constants/FileViews';
import FormStylesOptions from '../constants/FormStylesOptions';
import FieldPresentationModes from '../constants/FieldPresentationModes';
import FieldTypes from '../constants/FieldTypes';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import PageDisplayTypes from '../constants/PageDisplayTypes';
import PageLayoutModes from '../constants/PageLayoutModes';
import FieldProperties from '../constants/FieldProperties';
import FieldPositionProperties from '../constants/FieldPositionProperties';
import ChangelogTypes from '../constants/ChangelogTypes';


const { getCleanedJoyDoc, duplicateDocumentPage } = joyDocHelper;

const { generateTableLookup, getColumns, sortColumns, getRows, getRowOrder } = tableHelper;

/**
 * Helper function to choose the correct ID generation method based on useReadableIds prop
 * @param {boolean} useReadableIds - Whether to use readable IDs or object IDs
 * @param {Object} context - Context object for generateReadableId (fields, pages, views)
 * @param {string} type - Type for generateReadableId
 * @returns {string} - Generated ID
 */
export const generateId = (useReadableIds, context = null, type = null) => {
  if (useReadableIds) {
    return generateReadableId(context, type);
  }
  return generateObjectId();
};

/**
 * Helper function to choose the correct ID validation method based on useReadableIds prop
 * @param {boolean} useReadableIds - Whether to validate as readable ID or object ID
 * @param {string} id - ID to validate
 * @returns {boolean} - Whether the ID is valid
 */
export const validateId = (useReadableIds, id) => {
  if (useReadableIds) {
    return validateReadableId(id);
  }
  return validateObjectId(id);
};


export const getDataPathPopulatedFields = (data, fields) => {

  if (!data) return fields;

  return fields.map((field) => {

    if (!field.dataPath) return field;

    const nextField = { ...field };
    const path = nextField.dataPath.split('.');

    const value = getByPath(data, path, field.value);

    nextField.value = value;

    return nextField;

  });

};

/**
 * Create default page
 *
 * @param {Object} defaults
 * @param {boolean} useReadableIds
 * @return {Object}
 */
export const getDefaultJoyDocPage = (defaults = {}) => {

  const nextPage = {
    _id: generateObjectId(),
    name: 'New Page',
    width: 816,
    height: 1056,
    rowHeight: 8,
    cols: 8,
    fieldPositions: [],
    layout: PageLayoutModes.grid,
    presentation: FieldPresentationModes.normal,
    padding: 24,
  };

  if (defaults.displayType === PageDisplayTypes.pdf) {
    nextPage.layout = PageLayoutModes.float;
    nextPage.presentation = FieldPresentationModes.transparent;
    nextPage.rowHeight = 1;
    nextPage.cols = 816;
    nextPage.padding = 0;
  }

  return {
    ...nextPage,
    ...defaults
  };

};

/**
 * Generate the mobile view and add it to the file.
 *
 * This method only adds a mobile view to the file if
 * one does not already exist.
 *
 * @param {Object} file
 * @returns {Object} //File with the mobile view added to `file.views` property
 */
export const generateAndPopulateMobileViewForFile = (file) => {

  const nextFile = { ...file, views: file.views ? [...file.views] : [] };
  const existingMobileView = nextFile.views.find((view) => view?.type === FileViews.mobile);

  /**
   * Step 1: If mobile view already exists return unmodified file.
   */
  if (existingMobileView) {

    if (existingMobileView && !existingMobileView.pageOrder) {
      existingMobileView.pageOrder = getPageOrder(null, existingMobileView.pages);
    }

    return nextFile;

  }

  /**
   * Step 2: Generate new mobile view from existing primary view. Primary view is the `file.pages` property.
   */
  nextFile.views.push(generateMobileViewForFile(nextFile));

  return nextFile;

};

/**
 * Generate the mobile view and add specific mobile view properties
 *
 * @param {Object} file //Full file object
 * @returns {Object}
 */
export const generateMobileViewForFile = (file) => {

  return {
    _id: generateObjectId(),
    type: FileViews.mobile,
    pages: [...file.pages].map((page) => {
      return formatPageForMobileView({ ...page });
    }),
    pageOrder: getPageOrder(null, file.pages)
  };

};

/**
 * Apply the proper page defaults for a mobile view to existing page.
 *
 * @param {Object} page
 * @returns {Objectg}
 */
export const formatPageForMobileView = (page) => {
  return {
    ...page,
    fieldPositions: page.fieldPositions ? getMobileViewFormattedPageFieldPositions(page.fieldPositions) : [],
    cols: 1,
    rowHeight: 1,
    padding: 12,
    width: 816,
    height: 1056,
    layout: PageLayoutModes.grid,
    presentation: FieldPresentationModes.normal,
    backgroundImage: '',
    fileName: '',
    filePath: ''
  };
};

export const getMobileViewFormattedPageFieldPositions = (fieldPositions) => {

  const blockFieldStyles = [
    'fontSize',
    'fontColor',
    'fontStyle',
    'fontWeight',
    'textAlign',
    'textTransform',
    'textDecoration',
    'textOverflow',
    'borderColor',
    'borderRadius',
    'borderWidth',
    'backgroundColor',
    'padding',
  ];

  const richTextFieldStyles = [
    'backgroundColor',
    'borderColor',
    'borderRadius',
    'borderWidth',
  ];

  const mobileFields = [];

  /**
   * Step 1: Sort fieldPositions
   *
   * For example:
   * [
   *   { "y": 3, "x": 4 },
   *   { "y": 3, "x": 1 },
   *   { "y": 2, "x": 0 },
   *   { "y": 1, "x": 5.22 },
   *   { "y": 1, "x": 10 },
   *   { "y": 1, "x": 0 },
   * ]
   * will become the following after sort
   * [
   *   { "y": 1, "x": 0 },
   *   { "y": 1, "x": 5.22 },
   *   { "y": 1, "x": 10 },
   *   { "y": 2, "x": 0 },
   *   { "y": 3, "x": 1 },
   *   { "y": 3, "x": 4 }
   * ];
   */
  const sortedFieldPositions = sortFieldPositionsByXAndYCoordinates(fieldPositions);

  sortedFieldPositions.forEach((joySpecFieldPosition) => {

    const mobileFieldData = {
      ...joySpecFieldPosition,
      _id: joySpecFieldPosition._id,
      type: joySpecFieldPosition.type,
      field: joySpecFieldPosition.field,
      displayType: FieldDisplayTypes.original,
      x: 0,
      y: joySpecFieldPosition.y,
      width: 1,
      height: joySpecFieldPosition.height,
      titleDisplay: FormStylesOptions.displayInline
    };

    /**
     * Step 1.1: Pull mobile field specific properties off of fieldPosition
     */
    Object.keys(joySpecFieldPosition).forEach((key) => {

      if (mobileFieldData.type === FieldTypes.block && blockFieldStyles.indexOf(key) !== -1) mobileFieldData[key] = joySpecFieldPosition[key];

      if (mobileFieldData.type === FieldTypes.richText && richTextFieldStyles.indexOf(key) !== -1) mobileFieldData[key] = joySpecFieldPosition[key];

    });

    /**
     * Step 1.2 Hide chart lines on mobile
     */
    if (joySpecFieldPosition.type === FieldTypes.chart) mobileFieldData.primaryDisplayOnly = true;

    /**
     * Step 1.3 disabled Image field should not trigger file uploads upon click on mobile
     */
    if (joySpecFieldPosition.type === FieldTypes.image && joySpecFieldPosition.primaryDisplayOnly) mobileFieldData.primaryDisplayOnly = joySpecFieldPosition.primaryDisplayOnly;

    mobileFields.push(getDefaultWidthAndHeightForField(mobileFieldData));

  });

  /**
   * Step 2: Filter duplicates first
   */
  const filteredFields = filterDuplicatedFieldsGeneratedFromFieldPositionsForMobileView(mobileFields);

  /**
   * Step 3: Recalculate y positions for vertical stacking
   * 
   * Fields that were side-by-side on desktop (same y, different x) will overlap
   * in mobile view if we preserve desktop y values. We need to stack them vertically
   * by calculating each field's y position based on the previous field's position + height.
   */
  filteredFields.forEach((field, index) => {

    if (index === 0) {
      return;
    }

    // Subsequent fields: position them directly below the previous field
    const previousField = filteredFields[index - 1];
    
    const previousFieldBottom = previousField.y + previousField.height;
    
    field.y = previousFieldBottom;
    
  });

  return filteredFields;

};

/**
 * Filter Duplicated Fields Generated From Field Positions
 *
 * Why do duplicate fields get created from fieldPositions
 *
 * The same field can get added twice because we may have multiple fieldPositions belonging
 * to the same field. This can happen with dropdown, multiSelect, and table fields.
 *
 * Since the fields get generated by looping over the fieldPositions and not regular fields
 * we may encounter the the same field multiple times because it has multiple fieldPositions.
 *
 * @param {Array} fields
 * @returns {Array}
 */
export const filterDuplicatedFieldsGeneratedFromFieldPositionsForMobileView = (fields) => {

  const filteredFields = [];

  fields.forEach((field) => {

    const fieldIndex = filteredFields.findIndex((filtered) => filtered.field.toString() === field.field.toString());

    //Only add field to the list if it hasn't already been added
    if (fieldIndex === -1) filteredFields.push(field);

  });

  return filteredFields;

};

/**
 * Add proper cellValue and caluclatedCellValue to individually mapped
 * table cell fields. This is important to make sure cell forumlas are
 * properly calculated and added.
 *
 * * cellValue represents the normal value: =A1+A2
 * * calculatedCellValue represents the regular of the formula calculation: 10
 */
export const getTableFormattedPageFields = createSelector(
  (state) => state.fields,
  (fieldPositions) => {

    /**
     * We only want to generate the table value calculated lookup for each table field once.
     * We use these lookups because multiple table fieldPositions belonging to the same table
     * field will all use the same table value calculated lookup.
     */
    const tableFieldLookups = {};
    const tableFieldColumnLookups = {};

    const nextFieldPositions = [];

    [...fieldPositions].forEach((fieldPosition) => {

      const nextFieldPosition = { ...fieldPosition };

      if (fieldPosition.type === FieldTypes.table) {

        const { field, column, rowIndex, tableColumns, value, rowOrder, tableColumnOrder } = fieldPosition;

        /**
         * Value will include deleted rows as well. We are removing the deleted rows
         * since we want to compute the cellValue and calculatedCellValue only
         * with undeleted table rows
         */
        const rows = getRows(value);
        const nextRowOrder = getRowOrder(rowOrder, value);

        if (!tableFieldColumnLookups[field]) {
          const fieldColumnLookup = {};
          getColumns(sortColumns(tableColumns, tableColumnOrder)).forEach((column) => fieldColumnLookup[column._id] = column);
          tableFieldColumnLookups[field] = fieldColumnLookup;
        }

        if (!tableFieldLookups[field]) {
          const fieldTableLookup = generateTableLookup(rows, sortColumns(tableColumns, tableColumnOrder));
          tableFieldLookups[field] = fieldTableLookup;
        }

        /**
         * Ensure each table field has a properly formatted rowOrder
         */
        if (!nextFieldPosition.rowOrder) nextFieldPosition.rowOrder = nextRowOrder;

        /**
         * Individual Table Cell vs Entire Table
         *
         * Only add cellValue, calculatedCellValue, columnType, and column options to
         * individually mapped table cell fieldPositions. Regular table fields (entire table)
         * just simply use the fieldPositions.value parameter so we don't need the calculated
         * values, etc.
         *
         * We determine an individual table cell fieldPosition vs an entire table
         * fieldPosition by checking for column and rowIndex. Individual cells have
         * column and rowIndex but regular tables do not.
         *
         * Note:
         * rowIndex + 1 is used while calculating cell value since column operands
         * start from 1 instead of zero in the table lookups
         * (ie: [A1:{}, B1:{}] instead of [A0:{}, B0:{}])
         *
         * If tableRowIndex associated with a cell doesn't have a table row associated
         * with it (ie rows[rowIndex] is undefined) it means that particular row is deleted
         */
        if (tableFieldLookups[field] && column && typeof rowIndex === 'number') {

          /**
           * What is the difference between rowIndex and targetRowIndex?
           *
           * - The rowIndex on the fieldPosition is the saved index of where the rowId lives inside of the rowOrder.
           * - The targetRowIndex is the actual index of the row object inside the value (array of row data) of the field.
           *
           * We use the fieldPosition.rowIndex to get the rowId from the rowOrder. Then we use the rowId to locate
           * the assoicated row object data in the value property. The rowOrder and value (rows) do not follow the same
           * order. The rowOrder is updated when users insert rows, move rows up or down, etc. it manages where rows
           * are at in the list and how they should be rendered. This is why the rowIndex uses the rowOrder.
           */
          const targetColumn = tableFieldColumnLookups[field][column];
          const targetRowId = nextRowOrder[rowIndex];
          const targetRowIndex = rows.findIndex((row) => row._id === targetRowId);

          /**
           * Add proper rowId to the field position so that it can be used
           * inside the element for change handlers;
           */
          nextFieldPosition.rowId = targetRowId;

          /**
           * If rows[rowIndex] is undefined, it means the row that was present
           * at the particular rowIndex was deleted
          */

          nextFieldPosition.cellValue = (rows[targetRowIndex]?.cells[column] === 0 || rows[targetRowIndex]?.cells[column]) ? rows[targetRowIndex]?.cells[column] : '';

          //targetColumn will appear as undefined if a column is deleted
          if (targetColumn) {

            nextFieldPosition.calculatedCellValue = tableFieldLookups[field][`${targetColumn.operand}${targetRowIndex + 1}`];

            nextFieldPosition.columnType = targetColumn.type;

            nextFieldPosition.options = targetColumn.options;

            nextFieldPosition.columnIdentifier = targetColumn.identifier;

            //Push individual table cells if their associated column is not deleted
            nextFieldPositions.push(nextFieldPosition);

          }

        }
        else {
          //Push table fields with original display type
          nextFieldPositions.push(nextFieldPosition);
        }

      }
      else {
        //Push other fields
        nextFieldPositions.push(nextFieldPosition);
      }

    });

    return nextFieldPositions;

  }
);

/**
 * Selector for retrieving page fields
 *
 * Must pass the following values to this selector method:
 * * fieldPositions
 * * mode
 * * presentation
 *
 */
export const getPageFieldPositions = createSelector(
  getTableFormattedPageFields,
  (state) => state.fieldMeasurements,
  (fieldPositions, fieldHeightMeasurements) => {

    return fieldPositions.map((fieldPosition) => {

      const nextFieldPosition = { ...fieldPosition };
      if (fieldHeightMeasurements && fieldHeightMeasurements[fieldPosition._id]) {
        nextFieldPosition.height = fieldHeightMeasurements[fieldPosition._id].height;
      }

      return nextFieldPosition;

    });

  }
);

/**
 * Add position index to fields based on the y, x coordinate.
 *
 * This position index is used for things like setting the proper
 * z-index and tab index on fields.
 */
export const addPositionIndexesToPageFields = (pages) => {

  const nextPages = [...pages];
  const allPageFields = [];

  pages.forEach((page, pageIndex) => {

    if (!page.fields || page.fields.length < 0) return;

    const pageFields = sortFieldPositionsByXAndYCoordinates(page.fields);

    pageFields.forEach((field, fieldIndex) => {
      allPageFields.push({
        pageIndex,
        fieldIndex,
        field
      });
    });

  });

  allPageFields.forEach((fieldData, i) => {

    const { pageIndex, fieldIndex, field } = fieldData;

    const nextField = {
      ...field,
      zIndex: allPageFields.length - i
    };

    nextPages[pageIndex].fields = [...nextPages[pageIndex].fields];
    nextPages[pageIndex].fields[fieldIndex] = nextField;

  });

  return nextPages;

};

/**
 * Sort fields by their y and x coordinates
 *
 * @param {Array} fieldPositions
 * @returns {Array}
 */
export const sortFieldPositionsByXAndYCoordinates = (fieldPositions) => {

  const sortedFieldPositions = [...fieldPositions];

  sortedFieldPositions.sort((a, b) => {
    if (a.y === b.y) {
      return (a.x - b.x);
    } else if (a.y > b.y) {
      return 1;
    } else if (a.y < b.y) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortedFieldPositions;

};

/**
 * Adjusts a number by a certain percentage difference
 *
 * Specifies the number that should be adjusted
 * @param {Number} value
 *
 * Specifies the percentage to adjust the number by.
 * Examples, 0.2 represents 20%, -0.1 represents negative 10%, etc.
 * @param {Number} difference
 *
 * @return {Number}
 */
export const getPercentageAdjustedValue = (value, difference) => {
  const original = (value * difference) + value;
  return Number((original).toFixed(2));
};

/**
 * Adjusts a number back to it's original value before it was adjusted by
 * the target percentage difference.
 *
 * For calculations see: https://www.tutorialspoint.com/percentage_increase_and_decrease/finding_original_amount_given_result_percentage_increase_or_decrease.htm
 *
 * Specifies the number that has been adjusted.
 * @param {Number} value
 *
 * Specifies the percentage that was used to adjust the original value.
 * Examples, 0.2 represents 20%, -0.1 represents negative 10%, etc.
 * @param {Number} difference
 *
 * @return {Number}
 */

export const reversePercentageAdjustedValue = (value, difference) => {
  let original = value;

  /**
   * Handle negative percentage change
   */
  if (Math.sign(difference) === -1) {
    original = value / (1 - Math.abs(difference));
  }
  /**
   * Handle positive percentage change
   */
  else if (difference !== 0) {
    original = value / (1 + difference);
  }

  return Number((original).toFixed(2));
};

/**
 * Calculates the percentage adjustment that needs page background image and fieldPositions
 * need to be scaled up or down by in order for the page to fit within the max width and
 * height dimensions of the containing element of the SDK.
 *
 * Why do we choose only one of the percentages generated from the width or height for determining
 * how to scale the page?  We must use the same percentage in order to maintain proper ratios and
 * not cause distortion or stretching. This is primarily used with PDF Forms.
 *
 * We always return the smallest number using Math.min for the following scaling scenarios:
 * * Scaling up - width 0.2 (20%), height 0.1 (10%) in this scenario it means that you can scale up
 *   width 20% and height 10% more to fit available container space. But as mentioned previously
 *   you cannot use two different percentages. We must use the smaller 10% and not the 20% because if
 *   you tried to scale the height by 20% it would overflow the container boundary. We chose 10% because
 *   both height and width can scale by that percentage without overflowing the boundary.
 * * Scaling down - width -0.2 (-20%), height -0.1 (-10%) in this scenario it means that the page width
 *   and height need to be scaled down in order to fit within the containing element. We again choose the
 *   smaller number (-2.0), -0.2 is a smaller number than -0.1 because it's more negative. We must use the
 *   more negative -20% number because only that will ensure that both height and width are scaled down
 *   enough to both fit within the container element boundary.
 *
 * @param {Number} pageHeight //Actual saved page.height
 * @param {Number} pageWidth //Actual saved page.width
 * @param {Number} maxHeight //Containing element width in pixels
 * @param {Number} maxWidth //Containing element height in pixels
 * @returns {Number} adjustmentPercentage - The percentage adjustement that should be applied to page width, height, background, fields, etc.
 *
 */
export const calculatePagePercentageAdjustments = (pageHeight, pageWidth, maxHeight, maxWidth) => {

  /**
   * Calculate what percentage adjustement is required to fix within
   * the max dimensions.
   */
  const widthAdjustmentPercent = ((maxWidth - pageWidth) / pageWidth);
  const heightAdjustmentPercent = ((maxHeight - pageHeight) / pageHeight);

  let adjustementPercentage = 0;

  /**
   * If the precents are negative numbers (less than 0) that means
   * the page needs to be scaled down that amount in order to
   * fit within the boundaries.
   */
  if (widthAdjustmentPercent !== 0 || heightAdjustmentPercent !== 0) {

    /**
     * Why Math.min? We need to adjust the page by the most negative number.
     * For instance, if we have a -5 and -2 we need to adust the page by -5
     * and that is what Math.min will return since -5 is a smaller number than -2.
     */
    adjustementPercentage = Math.min(widthAdjustmentPercent, heightAdjustmentPercent);

  }

  return adjustementPercentage;

};

/**
 * This is used to prevent fields being dragged or resized off the page.
 *
 * @param {Object} fieldData
 * @param {Object} parentBoundingClientRect //Comes from calling reactElement.getBoundingClientRect();
 * @param {Object} pageStyles
 * @returns {Boolean}
 */
export const getValidAdjustedPageDropPosition = (fieldData, parentBoundingClientRect) => {

  /**
  * Parent right and bottom bounding cooridinates.
  *
  * We don't have to worry about top or left because
  * the drag and drop zone doesn't detect a drop if
  * the cursor isn't in a valid position for left and top
  * drop actions.
  */
  const parentPositionRightMax = parentBoundingClientRect.width;
  const parentPositionBottomMax = parentBoundingClientRect.height;

  /**
   * Adjust the x & y coordinates of the drop position to start at
   * the x & y (top left) position of parent element and not viewport (top left).
   */
  const nextFieldData = { ...fieldData };

  nextFieldData.x -= parentBoundingClientRect.x;
  nextFieldData.y -= parentBoundingClientRect.y;

  /**
   * Handle right and bottom adjustments to place
   * the new field properly inside the parent rect.
   */
  const totalFieldXRequirements = nextFieldData.x + nextFieldData.width;
  const totalFieldYRequirements = nextFieldData.y + nextFieldData.height;

  if (totalFieldXRequirements > parentPositionRightMax) {
    nextFieldData.x = parentPositionRightMax - (nextFieldData.width + 8);
  }

  if (totalFieldYRequirements > parentPositionBottomMax) {
    nextFieldData.y = parentPositionBottomMax - (nextFieldData.height + 8);
  }

  /**
   * X and Y cannot have any value less than 0
   */
  if (nextFieldData.x < 0) nextFieldData.x = 0;
  if (nextFieldData.y < 0) nextFieldData.y = 0;

  return nextFieldData;

};

/*
 * Get default width and height based on field types.
 *
 * Targets:
 * Page 816px x 1056px
 * Page Settings of 816 columns and rowHeight of 1px.
 */
export const getDefaultWidthAndHeightForField = (field) => {

  const { type, displayType } = field;

  if (type === FieldTypes.image) {
    field.width = 204;
    field.height = 180;
  }
  else if (type === FieldTypes.file) {
    field.width = 350;
    field.height = 180;
  }
  else if (type === FieldTypes.richText) {
    field.width = 204;
    field.height = 180;
  }
  else if (
    (
      type === FieldTypes.text
      || type === FieldTypes.number
      || type === FieldTypes.date
      || type === FieldTypes.dropdown
    )
    && (displayType === FieldDisplayTypes.original || !displayType)
  ) {
    field.width = 408;
    field.height = 64;
  }
  else if (
    type === FieldTypes.multiSelect
    && (displayType === FieldDisplayTypes.original || !displayType)
  ) {
    field.width = 408;
    field.height = 120;
  }
  else if (type === FieldTypes.block) {
    field.width = 408;
    field.height = 40;
  }
  else if (type === FieldTypes.textarea || type === FieldTypes.rte) {
    field.width = 408;
    field.height = 180;
  }
  else if (
    (type === FieldTypes.table && (displayType === FieldDisplayTypes.original || !displayType))
    || (type === FieldTypes.inputGroup && (displayType === FieldDisplayTypes.original || !displayType))
    || (type === FieldTypes.collection && (displayType === FieldDisplayTypes.original || !displayType))
    || (type === FieldTypes.collection && (displayType === FieldDisplayTypes.inputGroup || !displayType))
  ) {
    field.width = 816;
    field.height = 116;
  }
  else if (type === FieldTypes.pricingTable) {
    field.width = 816;
    field.height = 450;
  }
  else if (type === FieldTypes.rte) {
    field.width = 408;
    field.height = 64;
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text) {
    field.width = 200;
    field.height = 40;
  }
  else if (
    (type === FieldTypes.dropdown && displayType !== FieldDisplayTypes.original)
    || (type === FieldTypes.multiSelect && displayType !== FieldDisplayTypes.original)
    || (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.text)
  ) {
    field.width = 34;
    field.height = 34;
  }
  else if (type === FieldTypes.chart) {
    field.width = 408;
    field.height = 216;
  }
  else if (type === FieldTypes.signature) {
    field.width = 408;
    field.height = 180;
  }

  return field;
};

/**
 * Recalculate field position coordinates (width, height, x, y)
 * for the new columns and rowHeight values to match previous
 * equivalent dimensions.
 *
 * This is used so that even though you change the cols or rowHeight
 * the form fields still maintain their same position on the form.
 *
 * @param {Array} fields
 * @param {Number} newPageCols
 * @param {Number} newPageRowHeight
 * @param {Number} previousPageCols
 * @param {Number} previousPageRowHeight
 * @return {Array} //Array of page fields
 *
 */
export const recalculatePositionCoordinatesForPageFields = (
  fields,
  newPageCols,
  newPageRowHeight,
  previousPageCols,
  previousPageRowHeight
) => {

  if (!fields || fields.length < 1) return fields;

  return fields.map((field) => {
    return recalculatePositionCoordinatesForField(
      field,
      newPageCols,
      newPageRowHeight,
      previousPageCols,
      previousPageRowHeight
    );
  });

};

/**
 * Recalculate field position coordinates (width, height, x, y)
 * for the new columns and rowHeight values to match previous
 * equivalent dimensions.
 *
 * This is used so that even though you change the cols or rowHeight
 * the form fields still maintain their same position on the form.
 *
 * @param {Object} field
 * @param {Number} newPageCols
 * @param {Number} newPageRowHeight
 * @param {Number} previousPageCols
 * @param {Number} previousPageRowHeight
 * @return {Object}
 */
export const recalculatePositionCoordinatesForField = (
  field,
  newPageCols,
  newPageRowHeight,
  previousPageCols,
  previousPageRowHeight
) => {

  const nextField = { _id: field._id };

  /**
   * Re-calculate columns based on percentage difference
   */
  if (newPageCols && previousPageCols) {

    const colsDifference = (newPageCols - previousPageCols) / previousPageCols;

    nextField.x = getPercentageAdjustedValue(field.x, colsDifference);
    nextField.width = getPercentageAdjustedValue(field.width, colsDifference);

  }

  /**
   * Re-calculate rows based on percentage difference
   */
  if (newPageRowHeight && previousPageRowHeight) {

    const previousHeight = previousPageRowHeight * field.height; //field.height represents the number of rows
    const newHeight = newPageRowHeight * field.height;
    const rowHeightDifference = (previousHeight - newHeight) / newHeight;

    nextField.height = getPercentageAdjustedValue(field.height, rowHeightDifference);
    nextField.y = getPercentageAdjustedValue(field.y, rowHeightDifference);

  }

  return nextField;

};

/**
 * Update conditional logic field references when a field ID changes
 * 
 * This function finds all conditional logic throughout the document that references
 * the old field ID and updates them to use the new field ID.
 * 
 * @param {Array} fields - Array of all fields in the document
 * @param {String} oldFieldId - The old field ID that's being changed
 * @param {String} newFieldId - The new field ID to replace references with
 * @returns {Array} - Updated fields array with corrected logic references
 */
export const updateConditionalLogicFieldReferences = (fields, oldFieldId, newFieldId) => {
  
  if (!fields || !Array.isArray(fields) || !oldFieldId || !newFieldId || oldFieldId === newFieldId) {
    return fields;
  }

  return fields.map(field => {
    // Check if this field has conditional logic
    if (!field.logic || !field.logic.conditions || !Array.isArray(field.logic.conditions)) {
      return field;
    }

    // Check if any conditions reference the old field ID
    const hasReferences = field.logic.conditions.some(condition => condition.field === oldFieldId);
    
    if (!hasReferences) {
      return field;
    }

    // Create a deep copy of the field to update
    const updatedField = { ...field };
    updatedField.logic = { ...field.logic };
    updatedField.logic.conditions = field.logic.conditions.map(condition => {
      if (condition.field === oldFieldId) {
        return { ...condition, field: newFieldId };
      }
      return condition;
    });

    return updatedField;
  });
};

/**
 * Separate propertes for field and fieldPosition and return objects
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const separateFieldAndFieldPositionChangesForUpdate = (payload) => {

  /**
   * Why do I set the initla value of field and fieldPosition to false?
   *
   * I do this so that when we are evaluating if field or fieldPosition
   * should be updated in the object we can easily tell what object
   * the changed properties aply to in an if statement.
   */
  const changeObject = { fieldChanges: false, fieldPositionChanges: false };

  /**
   * Pull field specific properties off of fieldPosition
   */
  Object.keys(payload).forEach((key) => {

    // Special case: _id changes should target both fieldChanges._id and fieldPositionChanges.field
    if (key === '_id') {
      if (!changeObject.fieldChanges) changeObject.fieldChanges = {};
      if (!changeObject.fieldPositionChanges) changeObject.fieldPositionChanges = {};
      
      changeObject.fieldChanges[key] = payload[key];
      changeObject.fieldPositionChanges.field = payload[key];
    }
    else if (FieldProperties.indexOf(key) !== -1) {

      if (!changeObject.fieldChanges) changeObject.fieldChanges = {};

      changeObject.fieldChanges[key] = payload[key];
    } else if (FieldPositionProperties.indexOf(key) !== -1) {

      let targetKey = key;

      //Special case naming convention for fieldPosition properties to prevent conflict with field properties
      if (key === 'fieldPositionSchema') targetKey = 'schema';
      if (key === 'fieldPositionTableColumns') targetKey = 'tableColumns';

      if (!changeObject.fieldPositionChanges) changeObject.fieldPositionChanges = {};
      changeObject.fieldPositionChanges[targetKey] = payload[key];

    }
  });

  return changeObject;

};

/**
 * Separate propertes for field and fieldPosition and return objects
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const getFieldAndFieldPositionFromCombinedPayloadForCreation = (payload) => {

  const field = { _id: payload.field };
  const fieldPosition = { _id: payload._id };

  /**
   * Pull field specific properties off of fieldPosition
   */
  Object.keys(payload).forEach((key) => {

    //Type gets added to both field and fieldPosition
    if (key === 'type') {

      field[key] = payload[key];

      fieldPosition[key] = payload[key];

    } else if (FieldProperties.indexOf(key) !== -1) {

      field[key] = payload[key];

    } else if (FieldPositionProperties.indexOf(key) !== -1) {

      let targetKey = key;

      /**
       * Special case: fieldPositionTableColumns and fieldPositionSchema are renamed to tableColumns and schema
       * to prevent conflict with the field properties. But we want to keep the original property name for the fieldPosition
       * when splitting the field and fieldPosition objects.
       */
      if (key === 'fieldPositionSchema') targetKey = 'schema';
      if (key === 'fieldPositionTableColumns') targetKey = 'tableColumns';

      fieldPosition[targetKey] = payload[key];

    }

  });

  return { field, fieldPosition };

};

export const getDuplicatedPageAndChangelogsForDocument = (doc, fileId, pageId, useReadableIds) => {


  const result = duplicateDocumentPage(doc, fileId, pageId, {}, { useReadableIds });


  const nextDoc = result.doc;

  const changelogs = [];

  const { primaryPages, viewPages, fields } = result.changelogInfo;

  if (primaryPages) {
    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.pageCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      change: {
        page: primaryPages.page,
        targetIndex: primaryPages.targetIndex,
      },
      createdOn: new Date().getTime()
    });
  }

  if (viewPages && viewPages.length > 0) {
    viewPages.forEach(({ page, targetIndex, view, viewId }) => {
      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.pageCreate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        view,
        viewId,
        change: {
          page,
          targetIndex,
        },
        createdOn: new Date().getTime()
      });
    });
  }

  if (fields && fields.length > 0) {
    fields.forEach((newField) => {
      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fieldCreate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        change: newField,
        createdOn: new Date().getTime()
      });
    });
  }

  return { doc: nextDoc, changelogs };

};

/**
 * Get formatted fieldPosition for merging with fields 
 * 
 * This function is used to format the fieldPosition so that 
 * we can merge the field and fieldPosition into a single object
 * and use throughout the SDK. The reason we do this is because
 * we don't want any conflicting property names between field and fieldPosition
 */
export const getFormattedFieldPositionForMerging = (fieldPosition) => {

  const formattedFieldPosition = { ...fieldPosition };

  /**
   * Handle fieldPosition tableColumns (Table Fields)
   * 
   * Field and fieldPosition both have a tableColumns property. We want the field.tableColumns to take precedence
   * over the fieldPosition.tableColumns.
   * 
   * If fieldPosition.tableColumns is present, then we rename it to fieldPositionTableColumns
   * and allow that property to be used within the SDK.
   */
  const fieldPositionTableColumns = formattedFieldPosition.tableColumns ? {...formattedFieldPosition.tableColumns} : {};    
  if (fieldPosition.tableColumns) delete formattedFieldPosition.tableColumns //Delete so that it doesn't conflict with the field.tableColumns in the merged object below.

  /**
   * Handle fieldPosition schema (Collection Fields)
   * 
   * Field and fieldPosition both have a schema property when it's associated with a collection field. We want the 
   * field.schema to take precedence over the fieldPosition.schema.
   * 
   * If fieldPosition.schema is present, then we rename it to fieldPositionSchema
   * and allow that property to be used within the SDK.
   */
  const fieldPositionSchema = formattedFieldPosition.schema ? {...formattedFieldPosition.schema} : {};
  if (fieldPosition.schema) delete formattedFieldPosition.schema //Delete so that it doesn't conflict with the field.schema in the merged object below.


  return {
    ...formattedFieldPosition,

    //Add renamed properties for merging
    fieldPositionTableColumns,
    fieldPositionSchema,
  }

}


export const ensureTableColumnsHaveRequiredProperties = (tableColumns, newFieldPosition, fieldId, useReadableIds) => {

  const tableColumnsWithReadableIds = [...tableColumns].map(
    (column) => {

      const nextColumnId = useReadableIds ?
        generateReadableTableColumnId(newFieldPosition.field, newFieldPosition.tableColumns, column._id)
        : (column._id || generateObjectId());

      const nextColumn = { ...column, _id: nextColumnId };

      if (!nextColumn.identifier) nextColumn.identifier = `${fieldId}_column_${nextColumn._id}`;
      if (!nextColumn.width) nextColumn.width = 0;
      if (
        !newFieldPosition.columnType &&
        newFieldPosition.column &&
        newFieldPosition.column === nextColumn._id
      ) {
        newFieldPosition.columnType = nextColumn.type;
      }

      return nextColumn;
    }
  );


  return tableColumnsWithReadableIds;


}

export const ensureCollectionSchemaTableColumnsHaveRequiredProperties = (collectionSchema, newFieldPosition, fieldId, useReadableIds) => {

  const updatedSchema = JSON.parse(JSON.stringify(collectionSchema));

  const traverseAndUpdate = (tableId) => {

    const currentSchema = updatedSchema[tableId];
    
    if (!currentSchema || !Array.isArray(currentSchema.tableColumns)) return;

    currentSchema.tableColumns = ensureTableColumnsHaveRequiredProperties(
      currentSchema.tableColumns,
      { ...newFieldPosition, tableColumns: currentSchema.tableColumns },
      fieldId,
      useReadableIds
    );

    currentSchema.children?.forEach((childId) => traverseAndUpdate(childId));
  
  };

  const rootTableId = Object.keys(collectionSchema).find(
    (key) => collectionSchema[key].root === true
  );

  rootTableId && traverseAndUpdate(rootTableId);

  return updatedSchema;


}



/**
 * Get Clean JoyDoc Format
 *
 * This selector ensures a couple of things:
 * 1. Valid file inside the document.
 * 2. Valid page inside document file.
 * 3. All _id's (page, file, page, field) are in a valid format
 * 4. Row order of table field is free from deleted/duplicated rowIds
 *
 * @param {Object} doc
 * @returns {Object} doc
 */
export const getCleanedDocSelector = createSelector(
  (state) => state.doc,
  getCleanedJoyDoc
);

/**
 * Handles the retrieval of the active file from the document. Will
 * return the first file if no active file is set.
 */
export const getActiveFileSelector = createSelector(
  (state) => state.doc,
  (state) => state.activeFileId,
  (doc, activeFileId) => {

    if (!doc || !doc.files || doc.files.length < 1 || !doc.fields) return null;

    /**
     * Step 1: Select active file
     */
    const fileIndex = doc?.files.findIndex((file) => activeFileId === file._id);
    const activeFile = fileIndex === -1 ? doc?.files[0] : doc?.files[fileIndex];

    /**
     * Step 2: Add fields to file
     */
    const nextActiveFile = { ...activeFile };

    nextActiveFile.fields = !doc.fields || doc.fields.length < 1 ? [] :
      doc.fields.filter((field) => field.file === activeFile._id);

    return nextActiveFile;

  }
);

/**
 * Handles retrieval of the target view from a the file and
 * merges the fields and field positions within the view pages 
 * for that view. The result of the merge creates a page.fields 
 * that contains the field data and field position data for the 
 * view.
 * 
 * @param {Object} state
 * - state.doc (required)
 * - state.activeFileId (optional)
 * - state.view (optional)
 * @returns {Object} activeView
 */
export const getFileViewSelector = createSelector(
  getActiveFileSelector,
  (state) => state.view,
  (file, view) => {

    const targetFile = JSON.parse(JSON.stringify(file));

    /**
     * Step 1: Get active view
     */
    let activeView = targetFile;

    if (view === FileViews.mobile) {

      const nextFile = { ...targetFile, views: targetFile.views ? [...targetFile.views] : [] };
      const mobileView = nextFile.views.find((view) => view?.type === FileViews.mobile);

      const targetView = mobileView || generateMobileViewForFile(nextFile);

      activeView = {
        ...targetView,
        styles: targetFile.styles
      };

    }

    /**
     * Step 2: Merge fields and field postions for pages, header, and footer
     */
    const fieldLookup = {};
    file.fields.forEach((field) => fieldLookup[field._id] = field);

    /**
     * Step 2.1: Merge fields and field positions for pages
     */
    activeView.pages = [...activeView.pages].map((page) => {
      const nextPage = { ...page };

      /**
       * Step 2.1.1 Page.fields is the result of merging fieldPosition and field
       */
      nextPage.fields = nextPage.fieldPositions.map((fieldPosition) => {

        const targetField = fieldLookup[fieldPosition.field] || {};

        return {

          ...targetField,

          /**
           * IMPORTANT: DO NOT REMOVE.
           * - FieldPositon must come after targetField
           * - We format fieldPosition to ensure there are no conflicts with the field
           */
          ...getFormattedFieldPositionForMerging(fieldPosition),

        };


      });

      return nextPage;

    });

    /**
     * Step 2.2: Merge fields and field positions for header
     */
    if (activeView.header && activeView.header.fieldPositions) {

      activeView.header = { ...activeView.header };
      activeView.header.fields = activeView.header?.fieldPositions?.map((fieldPosition) => {
        const targetField = fieldLookup[fieldPosition.field] || {};

        return {
          ...targetField,

          /**
           * IMPORTANT: DO NOT REMOVE.
           * - FieldPositon must come after targetField
           * - We format fieldPosition to ensure there are no conflicts with the field
           */
          ...getFormattedFieldPositionForMerging(fieldPosition),
        };
      });

    }

    /**
     * Step 2.3: Merge fields and field positions for footer
     */
    if (activeView.footer && activeView.footer.fieldPositions) {

      activeView.footer = { ...activeView.footer };
      activeView.footer.fields = activeView.footer?.fieldPositions?.map((fieldPosition) => {
        const targetField = fieldLookup[fieldPosition.field] || {};

        return {
          ...targetField,

          /**
           * IMPORTANT: DO NOT REMOVE.
           * - FieldPositon must come after targetField
           * - We format fieldPosition to ensure there are no conflicts with the field
           */
          ...getFormattedFieldPositionForMerging(fieldPosition),
        };
      });

    }

    return activeView;

  }

);

/**
 * Used for getting/populating the proper views for a doc. Mainly used for use with TreeView
 */
export const getFileViews = createSelector(
  [(state) => state.file, (state) => state.pages],
  (file, pages) => {
    const existingViews = file.views || [];

    if (!file || !pages) return existingViews;

    // Create default desktop view
    const desktopView = {
      type: FileViews.desktop,
      pages,
      pageOrder: pages.map((page) => page._id),
      _id: `${FileViews.desktop}-${file._id}`
    };

    // Only add desktop if it doesn't exist
    const hasDesktop = existingViews.some((view) => view.type === FileViews.desktop);
    return hasDesktop ? existingViews : [desktopView, ...existingViews];
  }
);


/**
 * Updates all fieldPositions associated with a field in the document.
 * 
 * @param {Object} nextDoc - The document to update.
 * @param {string} fileId - The ID of the file to update.
 * @param {string} fieldId - The ID of the field to update.
 * @param {Object} changes - The changes to apply to the fieldPositions.
 */
export const updateAllFieldPositionAssociatedWithField = (nextDoc, fileId, fieldId, changes) => {

  const fileIndex = nextDoc.files.findIndex((file) => file._id === fileId);

  const updateFieldPositionsArray = (fieldPositions) => {
    if (!Array.isArray(fieldPositions)) return fieldPositions;
    return fieldPositions.map(fp =>
      fp.field === fieldId ? { ...fp, ...changes } : fp
    );
  }

  //Update primary view fieldPositions
  if (nextDoc.files[fileIndex].pages && nextDoc.files[fileIndex].pages.length > 0) {
    nextDoc.files[fileIndex].pages = nextDoc.files[fileIndex].pages?.map(page => ({
      ...page,
      fieldPositions: updateFieldPositionsArray(page.fieldPositions)
    }));
  }

  //Update primary view fieldPositions
  if (nextDoc.files[fileIndex].views && nextDoc.files[fileIndex].views.length > 0) {
    nextDoc.files[fileIndex].views = nextDoc.files[fileIndex].views.map(view => ({
      ...view,
      pages: view.pages?.map(page => ({
        ...page,
        fieldPositions: updateFieldPositionsArray(page.fieldPositions)
      }))
    }));
  }

  //Update header fieldPositions
  if (nextDoc.files[fileIndex].header && nextDoc.files[fileIndex].header.fieldPositions) {
    nextDoc.files[fileIndex].header = {
      ...nextDoc.files[fileIndex].header,
      fieldPositions: updateFieldPositionsArray(nextDoc.files[fileIndex].header.fieldPositions)
    };
  }

  //Update footer fieldPositions
  if (nextDoc.files[fileIndex].footer && nextDoc.files[fileIndex].footer.fieldPositions) {
    nextDoc.files[fileIndex].footer = {
      ...nextDoc.files[fileIndex].footer,
      fieldPositions: updateFieldPositionsArray(nextDoc.files[fileIndex].footer.fieldPositions)
    };
  }

  return nextDoc;
}